/**
 * 배포 후 옛 청크 깨짐 복구.
 *
 * 배포가 `client/dist`를 갈아끼운 뒤 새로고침하지 않은 탭이 페이지를 이동하면 옛 해시의 청크를 요청한다.
 * 그 파일이 없으면 동적 import가 거부되는데, 이를 사용자가 오류 카드로 보기 전에 **한 번의 조용한 리로드**로 흡수한다.
 *
 * - 진입점 두 곳이 같은 함수를 쓴다: Vite의 `vite:preloadError` 이벤트(main.tsx), 지연 로드 래퍼(lazy-with-recovery.ts)
 * - 리로드는 탭에서 60초에 1회(sessionStorage) — 새 index.html로도 실패하면 루프 대신 바운더리가 원인 안내를 띄운다
 * - 리로드는 `location.reload()`다(hardReload 아님). reload는 문서를 재검증하므로 새 index.html을 받는다(nginx 캐시 헤더와 무관)
 * - 모듈 플래그 `reloading`은 게이트(NavigationReloadGate)·래퍼·리스너 중 누가 먼저 리로드했든 중복 리로드를 막는다.
 *   리로드가 실제로는 일어나지 않을 수 있으므로(beforeunload 대화상자 취소, bfcache 복원) 플래그는
 *   RELOAD_GRACE_MS 뒤 자동 해제되고, pageshow(persisted)에서도 해제된다 — 영구 스피너 방지
 *
 * 설계: docs/update-chunk-recovery-plan.md
 */

/**
 * 쿨다운 기록 키. `hpm:` 접두사를 쓰지 않는다 — auth-store.clearAuth()가 로그아웃·refresh 실패(로그인 전 화면 매 로드)마다
 * `hpm:` 키를 전부 지우므로, 그 접두사를 쓰면 로그인 화면에서 쿨다운이 매번 사라져 청크 영구 결손 시 무한 리로드가 된다(2026-10-06 운영 실측).
 */
export const CHUNK_RECOVERY_KEY = 'chunk-recovery:reloaded-at';
export const CHUNK_RECOVERY_COOLDOWN_MS = 60_000;
/** 리로드 표시(reloading)를 유지하는 시간 — 이 안에 페이지가 교체되지 않으면 리로드가 무산된 것으로 본다 */
export const RELOAD_GRACE_MS = 10_000;

/** 브라우저·번들러별 동적 import/프리로드 실패 문구 (소문자 비교) */
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module', // Chrome·Edge
  'error loading dynamically imported module', // Firefox
  'importing a module script failed', // Safari
  'unable to preload css', // Vite 프리로드 헬퍼(CSS)
  'loading chunk', // webpack 계열 ChunkLoadError
  'loading css chunk',
];

/** 동적 import·프리로드 실패인가 (Chrome/Firefox/Safari/Vite 문구). Vite preloadError 이벤트의 payload도 같은 함수로 판정한다. */
export function isChunkLoadError(err: unknown): boolean {
  if (err == null) return false;
  const message = err instanceof Error ? err.message : String(err);
  const lower = message.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((p) => lower.includes(p));
}

let reloading = false;
let reloadingTimer: ReturnType<typeof setTimeout> | undefined;

/** 이 탭이 이미 리로드(또는 전체 로드)를 시작했는가 */
export function isReloading(): boolean {
  return reloading;
}

/**
 * 전체 로드를 시작한다고 표시 — NavigationReloadGate·recoverFromChunkError가 location.reload() 직전에 호출.
 * 리로드가 무산돼도 플래그가 고착되지 않도록 RELOAD_GRACE_MS 뒤 자동 해제한다.
 */
export function markReloading(): void {
  reloading = true;
  if (reloadingTimer !== undefined) clearTimeout(reloadingTimer);
  reloadingTimer = setTimeout(() => {
    reloading = false;
    reloadingTimer = undefined;
  }, RELOAD_GRACE_MS);
}

/**
 * 한 번만 조용히 리로드한다.
 * - 이미 리로드 중이면 true(호출자는 그대로 대기)
 * - 같은 탭에서 마지막 복구 후 60초 안이면 false(호출자가 에러를 그대로 던져 바운더리가 안내한다)
 * - sessionStorage 접근 실패는 "복구 가능"으로 취급한다
 */
export function recoverFromChunkError(): boolean {
  if (reloading) return true;

  const now = Date.now();
  try {
    const last = Number(window.sessionStorage.getItem(CHUNK_RECOVERY_KEY));
    const elapsed = now - last;
    if (last > 0 && elapsed >= 0 && elapsed < CHUNK_RECOVERY_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(CHUNK_RECOVERY_KEY, String(now));
  } catch {
    /* 저장소 차단(시크릿 모드 정책 등) — 기록 없이 복구를 시도한다 */
  }

  markReloading();
  console.warn('[chunk-recovery] 청크 로드 실패 — 새 버전을 받기 위해 페이지를 다시 불러옵니다');
  window.location.reload();
  return true;
}

let listenerInstalled = false;

/**
 * Vite 프리로드 헬퍼가 던지는 `vite:preloadError`를 받아 복구한다. main.tsx에서 1회 등록.
 * 복구(리로드)를 시작했으면 preventDefault로 에러 전파를 막는다 — 이때 해당 import는 undefined로 끝나므로
 * 지연 로드 래퍼가 리로드 중인 undefined 결과를 "대기"로 처리한다(lazy-with-recovery.ts).
 */
export function installPreloadErrorListener(): void {
  if (listenerInstalled || typeof window === 'undefined') return;
  listenerInstalled = true;
  // bfcache에서 복원된 페이지는 모듈 상태가 그대로라 reloading이 true로 남을 수 있다 — 해제한다
  window.addEventListener('pageshow', (event) => {
    if ((event as PageTransitionEvent).persisted) reloading = false;
  });
  window.addEventListener('vite:preloadError', (event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    if (!isChunkLoadError(payload)) return;
    if (recoverFromChunkError()) event.preventDefault();
  });
}
